"use client"
import type React from "react"
import { useState, useRef, useEffect } from "react"
import InputField from "../../../components/common/InputField"
import { createCourse, updateCourse, getCourseById, getAllGroups } from "./../../../api/courseService"
import {
  Upload,
  Bold,
  Italic,
  Strikethrough,
  Minus,
  Link2,
  Quote,
  Code2,
  Braces,
  Image as ImageIcon,
  Table2,
  List,
  ListOrdered,
  ListChecks,
  CircleHelp,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Maximize2,
  Minimize2,
} from "lucide-react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Markdown } from "@tiptap/markdown"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import { TableKit } from "@tiptap/extension-table"
import { TaskList, TaskItem } from "@tiptap/extension-list"
import TextAlign from "@tiptap/extension-text-align"
import Underline from "@tiptap/extension-underline"
import Hls from "hls.js"

interface CourseDetailsStepProps {
  data: any
  onChange: (data: any) => void
  onNext: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  userId?: string
}

const CourseDetailsStep: React.FC<CourseDetailsStepProps> = ({ 
  data, 
  onChange, 
  onNext,
  setLoading,
  setError,
  userId
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(data.language || "English")
  const [previewUrl, setPreviewUrl] = useState<string | null>(data.thumbnail || null)
  const formRef = useRef<HTMLFormElement>(null)
  const otherLanguageRef = useRef<HTMLInputElement>(null)
  const [initialData, setInitialData] = useState<any>(null)
  const [changedFields, setChangedFields] = useState<string[]>([])
  const [localData, setLocalData] = useState<any>(data)
  const [fetching, setFetching] = useState(false)
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [, setHls] = useState<Hls | null>(null)
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([])
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false)

// Fetch the list of channels (groups) a webinar can be tied to
  useEffect(() => {
    const fetchGroups = async () => {
      const response = await getAllGroups()
      if (response.status === 200) {
        setGroups(response.data || [])
      }
    }
    fetchGroups()
  }, [])

  // Helper function to determine if a URL is a video
  const isVideoUrl = (url: string): boolean => {
    // Check for data URLs
    if (url.startsWith('data:video')) {
      console.log("Detected data:video URL");
      return true;
    }
    
    // Check for streaming URLs
    if (url.includes('/stream/')) {
      console.log("Detected streaming video URL");
      return true;
    }
    
    // Check for file extensions in the URL
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const hasVideoExtension = videoExtensions.some(ext => url.includes(ext));
    if (hasVideoExtension) {
      console.log("Detected video extension in URL");
    }
    return hasVideoExtension;
  };

  // Initialize HLS player for streaming URLs
  useEffect(() => {
    if (!previewUrl || !isVideoUrl(previewUrl) || !previewUrl.includes('/stream/')) {
      return;
    }

    let newHls: Hls | null = null;

    if (Hls.isSupported() && videoRef.current) {
      newHls = new Hls({
        // Basic configuration
        enableWorker: true,
      });

      newHls.loadSource(previewUrl);
      newHls.attachMedia(videoRef.current);

      newHls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log("HLS manifest parsed successfully");
      });

      newHls.on(Hls.Events.ERROR, (event, data) => {
        console.error("HLS error:", data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              newHls?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              newHls?.recoverMediaError();
              break;
            default:
              newHls?.destroy();
              break;
          }
        }
      });

      setHls(newHls);
    } else if (videoRef.current?.canPlayType("application/vnd.apple.mpegurl")) {
      // For Safari
      videoRef.current.src = previewUrl;
    }

    return () => {
      if (newHls) {
        newHls.destroy();
      }
    };
  }, [previewUrl]);

  // Set preview URL from props data
  useEffect(() => {
    if (data.thumbnail && !previewUrl) {
      setPreviewUrl(data.thumbnail)
    }
    setLocalData(data)
  }, [data.thumbnail, previewUrl])

  useEffect(() => {
    setLocalData(data)
  }, [data])

  // Fetch course data when component mounts and course ID exists
  useEffect(() => {
    const fetchCourseData = async () => {
      if (data._id) {
        setFetching(true)
        try {
          const response = await getCourseById(data._id)
          if (response.status === 200 && response.data) {
            const fetchedData = response.data
            setInitialData(fetchedData)
            
            // Update form data with fetched values
            const updatedData = {
              ...data,
              title: fetchedData.title,
              description: fetchedData.description,
              language: fetchedData.language,
              availableOnDesktop: fetchedData.desktopAvailable,
              thumbnail: fetchedData.thumbnail,
              isVerificationNeeded: fetchedData.isVerificationNeeded,
              courseType: fetchedData.courseType || 'Course',
              webinarChannel: fetchedData.webinarChannel || null
            }
            
            onChange(updatedData)
            setPreviewUrl(fetchedData.thumbnail)
            
            // Set language state
            if (["English", "Hindi"].includes(fetchedData.language)) {
              setSelectedLanguage(fetchedData.language)
            } else {
              setSelectedLanguage("Others")
              updatedData.otherLanguage = fetchedData.language
            }
            
            // Reset change tracking
            setChangedFields([])
            setThumbnailFile(null)
          }
        } catch (error) {
          setError("Failed to fetch course data")
        } finally {
          setFetching(false)
        }
      }
    }
    
    fetchCourseData()
  }, [data._id])

  const handleInputChange = (field: string, value: any) => {
    // Track changed fields
    if (initialData) {
      const isChanged = initialData[field] !== value
      if (isChanged && !changedFields.includes(field)) {
        setChangedFields([...changedFields, field])
      } else if (!isChanged && changedFields.includes(field)) {
        setChangedFields(changedFields.filter(f => f !== field))
      }
    }
    
    setLocalData({ ...localData, [field]: value })
    onChange({ ...data, [field]: value })
  }

 const descriptionEditor = useEditor({
  extensions: [
    StarterKit,

    Markdown,

    Underline,

    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
    }),

    Image.configure({
      inline: false,
      allowBase64: true,
    }),

    TableKit.configure({
      table: {
        resizable: true,
      },
    }),

    TaskList,

    TaskItem.configure({
      nested: true,
    }),

    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
  ],

  content: data.description || "",
  contentType: "markdown",
  immediatelyRender: false,

  onUpdate: ({ editor }) => {
    const markdown = editor.getMarkdown()
    handleInputChange("description", markdown)
  },
})

useEffect(() => {
  if (!descriptionEditor) return

  const currentMarkdown = descriptionEditor.getMarkdown()

  if (currentMarkdown !== (data.description || "")) {
    descriptionEditor.commands.setContent(
      data.description || "",
      {
        contentType: "markdown",
      }
    )
  }
}, [data.description, descriptionEditor])

  const handleCourseTypeChange = (type: string) => {
    const updates: Record<string, any> = { courseType: type }
    if (type === "Course") {
      updates.webinarChannel = null
    }

    if (initialData) {
      let newChangedFields = [...changedFields]
      Object.entries(updates).forEach(([field, value]) => {
        const isChanged = initialData[field] !== value
        if (isChanged && !newChangedFields.includes(field)) {
          newChangedFields.push(field)
        } else if (!isChanged && newChangedFields.includes(field)) {
          newChangedFields = newChangedFields.filter(f => f !== field)
        }
      })
      setChangedFields(newChangedFields)
    }

    setLocalData({ ...localData, ...updates })
    onChange({ ...data, ...updates })
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      console.log("File selected:", file.name, file.type, file.size);
      
      if (!file.type.match('image.*') && !file.type.match('video.*')) {
        setError("Only image and video files are supported")
        return
      }
      
      const maxSize = file.type.match('image.*') ? 10 * 1024 * 1024 : 100 * 1024 * 1024
      if (file.size > maxSize) {
        setError(file.type.match('image.*') 
          ? "Image size should be less than 10MB" 
          : "Video size should be less than 100MB")
        return
      }
      
      // Set the file and mark as changed
      setThumbnailFile(file)
      if (!changedFields.includes('thumbnail')) {
        setChangedFields([...changedFields, 'thumbnail'])
      }
      
      const reader = new FileReader()
      reader.onloadend = () => {
        console.log("FileReader result type:", typeof reader.result);
        console.log("FileReader result starts with:", String(reader.result).substring(0, 50));
        // console.log("Full FileReader result:", String(reader.result));
        setPreviewUrl(reader.result as string)
        handleInputChange("thumbnail", reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    if (!formRef.current) return false
    const current = localData
    
    if (!current.title || !current.description) {
      setError("Please fill all required fields")
      return false
    }
    
    // Only require thumbnail for new courses, not for updates
    if (!current._id && !thumbnailFile && !previewUrl) {
      setError("Please upload a cover image or video")
      return false
    }
    
    if (selectedLanguage === "Others" && !current.otherLanguage) {
      setError("Please specify the language")
      otherLanguageRef.current?.focus()
      return false
    }
    
    if (!userId) {
      setError("User authentication failed. Please try logging in again.")
      return false
    }

    if (current.courseType === "Webinar" && !current.webinarChannel) {
      setError("Please select a webinar channel")
      return false
    }

    return true
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    
    if (!validateForm()) return
    
    try {
      setLoading(true)
      setError(null)
      
      if (!userId) {
        setError("User authentication failed. Please try logging in again.")
        return
      }
      
      const current = localData
      const languageValue = selectedLanguage === "Others" ? current.otherLanguage : selectedLanguage
      
      // If course exists, update only changed fields
      if (data._id) {
        // Only proceed if there are actual changes
        if (changedFields.length === 0) {
          onNext()
          return
        }
        
       const payload: any = {
        id: data._id,
        user_id: userId,
      }
        
        // Add only changed fields to payload
        changedFields.forEach(field => {
          if (field === 'thumbnail' && thumbnailFile) {
            payload.file = thumbnailFile
          } else if (field === 'availableOnDesktop') {
            payload.desktopAvailable = current.availableOnDesktop
          } else if (field === 'isVerificationNeeded') {
            payload.isVerificationNeeded = current.isVerificationNeeded
          } else if (field === 'language') {
            payload.language = languageValue
          } else if (field !== 'thumbnail') {
            payload[field] = current[field]
          }
        })
        
        const response = await updateCourse(payload)
         
        if (response.status >= 400) {
          setError(response.message || "Failed to update course")
          return
        }
        
        const updatedData = {
          ...data,
          title: response.data?.title || data.title,
          thumbnail: response.data?.thumbnail || data.thumbnail,
          language: languageValue
        }
        
        // Reset change tracking after successful update
        setChangedFields([])
        setThumbnailFile(null)
        
        onChange(updatedData)
        onNext()
      } else {
        // Create new course
        const payload = {
          created_by: userId,
          title: String(current.title || ""),
          description: String(current.description || ""),
          language: String(languageValue || "English"),
          desktopAvailable: Boolean(current.availableOnDesktop),
          file: thumbnailFile,
          isVerificationNeeded: Boolean(current.isVerificationNeeded),
          courseType: current.courseType || 'Course',
          webinarChannel: current.courseType === 'Webinar' ? current.webinarChannel : null
        }
        
        const response = await createCourse(payload)
         
        if (response.status >= 400) {
          setError(response.message || "Failed to create course")
          return
        }
        
        const updatedData = {
          ...data,
          _id: response.data?._id,
          title: response.data?.title,
          thumbnail: response.data?.thumbnail,
          language: languageValue
        }
        
        onChange(updatedData)
        onNext()
      }
    } catch (err) {
      console.error("Course processing error:", err)
      setError("Failed to process course. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (fetching) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }


  return (
    
    <form 
      ref={formRef}
      id="course-details-form" 
      onSubmit={handleSubmit} 
      className="space-y-8"
      noValidate
    >
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Begin your course journey
        </h2>
        <p className="text-gray-600">
          Upload cover media and introduce your course to spark interest
        </p>
      </div>
      
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Cover image or video <span className="text-red-500">*</span>
        </label>
      <div className="border-2 border-dashed border-gray-300 rounded-xl w-full max-w-full overflow-hidden">
          <input
            type="file"
            accept="image/*,video/*"
            onChange={handleImageUpload}
            className="hidden"
            id="cover-upload"
            required={!data._id && !previewUrl}
          />
          <label htmlFor="cover-upload"  className="block w-full max-w-full cursor-pointer">
            {previewUrl ? (
              <div className="relative w-full aspect-video max-h-96 overflow-hidden rounded-lg">
                {(() => {
                  const isVideo = isVideoUrl(previewUrl);
                  console.log("Media type detection:", { previewUrl, isVideo });
                  return !isVideo ? (
                    <>
                      <div className="absolute inset-0 bg-gray-100">
                        <img
                          src={previewUrl}
                          alt="Course cover preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            console.log("Image failed to load, might be a video:", previewUrl);
                            target.onerror = null;
                            target.src = 'https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Image+Not+Found';
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPreviewUrl(null)
                          setThumbnailFile(null)
                          if (changedFields.includes('thumbnail')) {
                            setChangedFields(changedFields.filter(f => f !== 'thumbnail'))
                          }
                        }}
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-sm transition-colors z-10"
                        aria-label="Remove image"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <div className="relative w-full aspect-video max-h-96 overflow-hidden rounded-lg bg-black">
                      <video 
                        ref={videoRef}
                        src={previewUrl.includes('/stream/') ? undefined : previewUrl}
                        controls
                        className="w-full h-full"
                        onError={(e) => {
                          console.log("Video failed to load:", previewUrl);
                        }}
                        onLoadedData={(e) => {
                          console.log("Video loaded successfully:", previewUrl);
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPreviewUrl(null)
                          setThumbnailFile(null)
                          if (changedFields.includes('thumbnail')) {
                            setChangedFields(changedFields.filter(f => f !== 'thumbnail'))
                          }
                        }}
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-sm transition-colors z-10"
                        aria-label="Remove video"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })()}
              </div>
            ) : (
          <div
            className="
              relative
              w-full
              max-w-full
              min-h-[220px]
              sm:min-h-[260px]
              md:min-h-[300px]
              rounded-xl
              bg-gradient-to-br from-blue-50 to-indigo-50
              flex flex-col items-center justify-center
              px-4 py-6 sm:p-8
              border-2 border-dashed border-blue-300
              text-center
              box-border
            "
          >
            <div className="bg-blue-100 rounded-full p-3 mb-3 sm:mb-4">
              <Upload className="text-blue-600" size={24} />
            </div>

            <p className="text-gray-700 font-semibold text-sm sm:text-base mb-1">
            Click to upload cover media
          </p>

          <p className="text-xs sm:text-sm text-gray-600 mb-1">
            Recommended: 1280 × 720 px (16:9)
          </p>

          <p className="text-[11px] sm:text-xs text-gray-500 leading-tight">
            Upload the cover in 16:9 aspect ratio.<br />
            Max photo size: 10 MB · Max video size: 100 MB
          </p>

          </div>
              
            )}
          </label>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <InputField
          type="text"
          label="Course title"
          name="title"
          value={data.title || ""}
          onChange={(e) => handleInputChange("title", e.target.value)}
          required
          placeholder="Enter an engaging title for your course"
        />
      </div>
      
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">
    <span>Course description</span>
    <span className="text-red-500">*</span>
  </label>

<div
  className={`course-description-editor ${
    isEditorFullscreen
      ? "course-description-editor-fullscreen"
      : ""
  }`}
>
  
  {/* Toolbar */}
<div className="course-description-toolbar">

  {/* Bold */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleBold().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("bold") ? "active" : ""
    }`}
    title="Bold"
  >
    <Bold size={17} strokeWidth={2.5} />
  </button>

  {/* Italic */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleItalic().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("italic") ? "active" : ""
    }`}
    title="Italic"
  >
    <Italic size={17} strokeWidth={2.5} />
  </button>

  {/* Strikethrough */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleStrike().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("strike") ? "active" : ""
    }`}
    title="Strikethrough"
  >
    <Strikethrough size={17} strokeWidth={2.5} />
  </button>

  {/* Horizontal Line */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().setHorizontalRule().run()
    }
    className="editor-tool"
    title="Horizontal Line"
  >
    <Minus size={18} strokeWidth={2.5} />
  </button>

  <div className="editor-toolbar-divider" />

  {/* Link */}
  <button
    type="button"
    onClick={() => {
      const previousUrl =
        descriptionEditor?.getAttributes("link").href

      const url = window.prompt(
        "Enter URL",
        previousUrl || "https://"
      )

      if (url === null) return

      if (url === "") {
        descriptionEditor
          ?.chain()
          .focus()
          .extendMarkRange("link")
          .unsetLink()
          .run()

        return
      }

      descriptionEditor
        ?.chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run()
    }}
    className={`editor-tool ${
      descriptionEditor?.isActive("link") ? "active" : ""
    }`}
    title="Insert Link"
  >
    <Link2 size={18} strokeWidth={2.5} />
  </button>

  {/* Blockquote */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleBlockquote().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("blockquote") ? "active" : ""
    }`}
    title="Blockquote"
  >
    <Quote size={18} strokeWidth={2.5} />
  </button>

  {/* Inline Code */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleCode().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("code") ? "active" : ""
    }`}
    title="Inline Code"
  >
    <Code2 size={18} strokeWidth={2.5} />
  </button>

  {/* Code Block */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleCodeBlock().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("codeBlock") ? "active" : ""
    }`}
    title="Code Block"
  >
    <Braces size={18} strokeWidth={2.5} />
  </button>

  {/* Image */}
  <button
    type="button"
    onClick={() => {
      const url = window.prompt("Enter image URL")

      if (url) {
        descriptionEditor
          ?.chain()
          .focus()
          .setImage({ src: url })
          .run()
      }
    }}
    className="editor-tool"
    title="Insert Image"
  >
    <ImageIcon size={18} strokeWidth={2.3} />
  </button>

  {/* Table */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor
        ?.chain()
        .focus()
        .insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true,
        })
        .run()
    }
    className="editor-tool"
    title="Insert Table"
  >
    <Table2 size={18} strokeWidth={2.3} />
  </button>

  {/* Bullet List */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleBulletList().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("bulletList") ? "active" : ""
    }`}
    title="Bullet List"
  >
    <List size={18} strokeWidth={2.3} />
  </button>

  {/* Numbered List */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleOrderedList().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("orderedList") ? "active" : ""
    }`}
    title="Numbered List"
  >
    <ListOrdered size={18} strokeWidth={2.3} />
  </button>

  {/* Checklist */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().toggleTaskList().run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive("taskList") ? "active" : ""
    }`}
    title="Checklist"
  >
    <ListChecks size={18} strokeWidth={2.3} />
  </button>

  <div className="editor-toolbar-divider" />

  {/* Help */}
  <button
    type="button"
    onClick={() => {
      window.alert(
        "Course Description Editor\n\n" +
        "Use the toolbar to format your course description.\n\n" +
        "• Bold / Italic / Strike\n" +
        "• Links and blockquotes\n" +
        "• Code and code blocks\n" +
        "• Images and tables\n" +
        "• Bullet, numbered and checklist lists\n" +
        "• Text alignment\n" +
        "• Undo / Redo"
      )
    }}
    className="editor-tool"
    title="Editor Help"
  >
    <CircleHelp size={18} strokeWidth={2.3} />
  </button>

  <div className="editor-toolbar-spacer" />

  {/* Align Left */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor
        ?.chain()
        .focus()
        .setTextAlign("left")
        .run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive({ textAlign: "left" })
        ? "active"
        : ""
    }`}
    title="Align Left"
  >
    <AlignLeft size={18} strokeWidth={2.3} />
  </button>

  {/* Align Center */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor
        ?.chain()
        .focus()
        .setTextAlign("center")
        .run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive({ textAlign: "center" })
        ? "active"
        : ""
    }`}
    title="Align Center"
  >
    <AlignCenter size={18} strokeWidth={2.3} />
  </button>

  {/* Align Right */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor
        ?.chain()
        .focus()
        .setTextAlign("right")
        .run()
    }
    className={`editor-tool ${
      descriptionEditor?.isActive({ textAlign: "right" })
        ? "active"
        : ""
    }`}
    title="Align Right"
  >
    <AlignRight size={18} strokeWidth={2.3} />
  </button>

  <div className="editor-toolbar-divider" />

  {/* Undo */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().undo().run()
    }
    className="editor-tool"
    title="Undo"
  >
    ↶
  </button>

  {/* Redo */}
  <button
    type="button"
    onClick={() =>
      descriptionEditor?.chain().focus().redo().run()
    }
    className="editor-tool"
    title="Redo"
  >
    ↷
  </button>

  {/* Fullscreen */}
  <button
    type="button"
    onClick={() =>
      setIsEditorFullscreen(!isEditorFullscreen)
    }
    className="editor-tool"
    title={
      isEditorFullscreen
        ? "Exit Fullscreen"
        : "Fullscreen"
    }
  >
    {isEditorFullscreen ? (
      <Minimize2 size={18} strokeWidth={2.3} />
    ) : (
      <Maximize2 size={18} strokeWidth={2.3} />
    )}
  </button>

</div>

  {/* Editor */}
  <EditorContent
    editor={descriptionEditor}
    className="course-description-content"
  />

  {/* Footer */}
  <div className="course-description-footer">
    Describe what students will learn in this course.
  </div>

</div> 
</div>    
      
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <label className="block text-sm font-semibold text-gray-700 mb-4">Course language</label>
        <div className="flex flex-wrap gap-3">
          {["English", "Hindi", "Others"].map((language) => (
            <div key={language} className="flex items-center">
              <button
                type="button"
                onClick={() => {
                  setSelectedLanguage(language)
                  handleInputChange("language", language)
                }}
                className={`px-6 py-3 rounded-xl border-2 transition-all duration-200 font-medium ${
                  selectedLanguage === language
                    ? "bg-blue-50 border-blue-500 text-blue-700 shadow-md transform scale-105"
                    : "bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/30"
                }`}
              >
                {language === "English" && "🇺🇸"} 
                {language === "Hindi" && "🇮🇳"} 
                {language === "Others" && "🌐"} {language}
              </button>
              {language === "Others" && selectedLanguage === "Others" && (
                <input
                  ref={otherLanguageRef}
                  type="text"
                  value={data.otherLanguage || ""}
                  onChange={(e) => handleInputChange("otherLanguage", e.target.value)}
                  placeholder="Specify language"
                  className="ml-3 px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Desktop Availability</h3>
            <p className="text-sm text-gray-600">Make this course available on desktop platforms</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={data.availableOnDesktop || false}
              onChange={(e) => handleInputChange("availableOnDesktop", e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
      
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Verification Required</h3>
            <p className="text-sm text-gray-600">Require verification before learners can enroll or access this course</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={data.isVerificationNeeded || false}
              onChange={(e) => handleInputChange("isVerificationNeeded", e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <label className="block text-sm font-semibold text-gray-700 mb-4">Course Type</label>
        <div className="flex flex-wrap gap-3">
          {["Course", "Webinar"].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleCourseTypeChange(type)}
              className={`px-6 py-3 rounded-xl border-2 transition-all duration-200 font-medium ${
                (data.courseType || "Course") === type
                  ? "bg-blue-50 border-blue-500 text-blue-700 shadow-md transform scale-105"
                  : "bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/30"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {data.courseType === "Webinar" && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
          <label className="block text-sm font-semibold text-gray-700 mb-4">Webinar Channel</label>
          <select
            value={data.webinarChannel || ""}
            onChange={(e) => handleInputChange("webinarChannel", e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a channel</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>
      )}
    </form>
  )
}

export default CourseDetailsStep